// The live cart value, the session depot's basket lines summed at
// the prices the server locked on them (`cartValue`). Read by the header cart trigger
// (through `useCartPanel`), the cart side panel's footer and the scanner's cart chip; the
// upsell summaries apply the same rule through `useCartLinesLoaded`.
//
// `null` (shown as UNKNOWN_TOTAL) under the same rule as `useCartCaseCount`: while the
// session's cart has not loaded, or when its read failed. With no session cart (no depot, or a
// staff user with no store account picked, Enabler E8-G DA-10) there is none to load, and the
// value is the store's. A SKU just added has no locked
// price until the server answers, so `settledCartValue` leaves it out while its write is
// pending (the value holds instead of flickering to unknown on every add); a line with no
// price and no write in flight makes the value unknown.
//
// The store selectors return the lines array, the pending set and a boolean, never a fresh
// object, so the subscription is stable; the value is memoised on them.
import { useMemo } from 'react';
import type { MoneyValue } from '../shared/money';
import { settledCartDeposit, settledCartSaving, settledCartValue } from './cartMoney';
import { cartSendsSavings } from './cartTotals';
import { useBasketStore } from './useBasketStore';
import { useSessionCartTarget } from './sessionCart';

/** Whether the store's lines are the session's cart as loaded: false while it has not loaded
 *  or its read failed; true when there is no session cart to read. The one rule the counts,
 *  the value and the side panel follow. A host that defers the sum (UpsellModal, mounted for
 *  the whole app and closed almost all the time) reads this and sums only when it draws. */
export function useCartLinesLoaded(): boolean {
  const noCart = useSessionCartTarget() === null;
  return useBasketStore((s) => !s.error && (noCart || s.hydrated));
}

export function useCartValue(): MoneyValue | null {
  const lines = useBasketStore((s) => s.lines);
  const pending = useBasketStore((s) => s.pending);
  const loaded = useCartLinesLoaded();
  return useMemo(
    () => (loaded ? settledCartValue(lines, pending) : null),
    [loaded, lines, pending],
  );
}

/** ADO-2246: the live deposit total, over the same lines and under the same loaded and pending
 *  rules as `useCartValue` (it counts what the goods total counts). `null` is unknown, shown
 *  as UNKNOWN_TOTAL. */
export function useCartDeposit(): MoneyValue | null {
  const lines = useBasketStore((s) => s.lines);
  const pending = useBasketStore((s) => s.pending);
  const loaded = useCartLinesLoaded();
  return useMemo(
    () => (loaded ? settledCartDeposit(lines, pending) : null),
    [loaded, lines, pending],
  );
}

/** Pricing migration 18 (ADO-2249, Task 9.2): the live cart's savings. `sent` is false while no
 *  line carries `priceKind` (DEC-6) or VITE_REAL_PRICES_ENABLED is off (DEC-18), and then nothing
 *  is shown. `total` counts the lines the goods and deposit totals count (`settledCartSaving`): a
 *  line just added, with no `priceKind` until the server answers, is left out while its write is
 *  pending, so the figure holds; any other line whose saving is unknown makes it the dash (Task
 *  8.1). Both wait for the lines to load, as the other totals do. */
export interface CartSavings {
  readonly sent: boolean;
  readonly total: MoneyValue | null;
}

export function useCartSavings(): CartSavings {
  const lines = useBasketStore((s) => s.lines);
  const pending = useBasketStore((s) => s.pending);
  const loaded = useCartLinesLoaded();
  return useMemo(
    () =>
      loaded && cartSendsSavings(lines)
        ? { sent: true, total: settledCartSaving(lines, pending) }
        : { sent: false, total: null },
    [loaded, lines, pending],
  );
}
